import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../Footer';

/* ── Grid background ── */
const GridBg = () => (
  <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
    <div
      className="absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage: `linear-gradient(#54E346 1px, transparent 1px), linear-gradient(90deg, #54E346 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
  </div>
);

/* ── Feature card ── */
const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    className="relative p-6 rounded-2xl border border-dark-border/40 bg-dark-card/50 backdrop-blur-sm hover:border-primary/30 transition-colors duration-300 group"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
  >
    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
      {icon}
    </div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

/* ── Product Page ── */
const Product = () => (
  <div className="bg-dark text-white min-h-screen relative">
    <GridBg />

    {/* Top bar — logo + back nav */}
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <div className="flex items-center justify-between">
        {/* Brand logo */}
        <Link to="/" aria-label="OpticVance home">
          <img
            src="/logo.svg"
            alt="OpticVance logo"
            className="w-40 h-40 sm:w-44 sm:h-44"
            style={{ display: 'block', filter: 'drop-shadow(0 0 44px rgba(8,203,0,0.6))' }}
          />
        </Link>

        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-primary transition-colors text-sm"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>
    </div>

    {/* Hero section */}
    <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase border border-primary/30 text-primary mb-6">
          Opticore
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Welcome to{' '}
          <span className="bg-gradient-to-r from-primary to-[#54E346] bg-clip-text text-transparent">
            OpticVance
          </span>
        </h1>
        <p className="text-gray-400 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed mb-10">
          AI-powered analytical intelligence platform that transforms raw data into
          actionable business insights. Built for teams that move fast and think big.
        </p>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="http://app.opticvance.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 rounded-xl font-bold text-black transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
            style={{ background: 'linear-gradient(135deg, #08CB00, #54E346)' }}
          >
            Get Started
          </a>
          
        </div>
      </motion.div>

      {/* Decorative glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(8,203,0,0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
        aria-hidden="true"
      />
    </section>

    {/* Key features grid */}
    <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <motion.div
        className="text-center mb-14"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
          Why <span className="text-primary">OpticVance</span>?
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Everything you need to harness the power of your data, all in one platform.
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <FeatureCard
          delay={0}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-6 h-6">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          }
          title="Data Integration"
          description="Connect and unify data from 200+ sources in minutes. No more data silos — one platform, complete visibility."
        />
        <FeatureCard
          delay={0.1}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-6 h-6">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          }
          title="AI-Powered Analytics"
          description="Machine learning models that learn from your data patterns and surface insights humans would miss."
        />
        <FeatureCard
          delay={0.2}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-6 h-6">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
          }
          title="Real-Time Dashboards"
          description="Beautiful, interactive dashboards that update in real-time. Share insights across your entire organization."
        />
        <FeatureCard
          delay={0.3}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-6 h-6">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          }
          title="Predictive Analytics"
          description="Forecast trends, anticipate risks, and make data-driven decisions before your competitors do."
        />
        <FeatureCard
          delay={0.4}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-6 h-6">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          }
          title="Enterprise Security"
          description="SOC 2 Type II certified. End-to-end encryption, role-based access control, and full audit trails."
        />
        <FeatureCard
          delay={0.5}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-6 h-6">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
          }
          title="Team Collaboration"
          description="Built for teams. Share reports, annotate insights, and collaborate in real-time across departments."
        />
      </div>
    </section>

    {/* Product placeholder / integration point */}
    <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <motion.div
        className="rounded-2xl border border-dashed border-primary/30 bg-primary/[0.03] p-12 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="1.5" className="w-8 h-8">
            <rect x="5" y="5" width="14" height="14" rx="2" />
            <circle cx="12" cy="12" r="3" />
            <path d="M12 1v4M12 19v4M1 12h4M19 12h4" strokeOpacity="0.5" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white mb-3">Product Integration Zone</h3>
        <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
          This section is reserved for the Tech Solution Person to integrate the core
          OpticVance product experience. Please coordinate with your development team
          to provide access credentials, integration requirements, and frontend/backend
          code references.
        </p>
      </motion.div>
    </section>

    {/* Contact CTA removed */}

    <Footer />
  </div>
);

export default Product;
